import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Grid,
    Typography,
    Button,
    TextField,
    Select,
    Collapse,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    InputAdornment,
    Tooltip,
    Checkbox,
    Divider,
    ListItemIcon,
    CircularProgress,
    Alert
} from "@mui/material";
import CurrencyFormat from "react-currency-format";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    mbxUserAtom,
    allActiveLocationsAtom,
    allCategoriesAtom,
    allServicesAtom,
    deleteServiceInAllServicesAtom,
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    confirmationFunctionAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    conformationDialogIsSubmittingAtom,
    updateServiceInAllServicesAtom
} from "../atoms/atoms";
import { selectedServiceAtom } from "../atoms/servicesTable";
import { userPageSnackbarAtom } from "../atoms/usersTable";
import { postApi, putApi, getJsonApi } from "../modules/AmplifyServices";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { DialogTheme } from "../styles/DialogTheme";
import { ExpandLess, ExpandMore, Info } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AddCategoriesOnTheFlyDialog from "./AddCategoriesOnTheFlyDialog";
import InfoTooltip from "../utils/UI/InfoTooltip";
import {
    convertToRRuleString,
    getCourseSchedulesDiff,
    getDirtyValues,
    getSettingsFromRrule,
    defaultRRule,
    serviceTypeFieldMap,
    timestampToTimeString,
    createScheduleDiffObjectForDateRangeDeletion,
    getYYYYMMDD
} from "../utils/courseUtils";
import CoursesrRuleSection from "./CoursesrRuleSection";
import ValidateCourseDatesModal from "../user/components/ValidateCourseDatesModal";
import { addMonths } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import {
    DEFAULT_ALLOWED_MINIMUM_COURSE_CAPACITY,
    DEFAULT_COURSE_CAPACITY,
    MB_SERVICE_CLASSIFICATION_GROUP,
    MB_SERVICE_CLASSIFICATION_SIMPLE
} from "../utils/Constants";
import { getBaseDate } from "../utils";
import { companyCanUseCourses } from "../modules/FeatureGatingModule";
import {
    auditServiceCreate,
    auditServiceDelete,
    auditServiceUpdate
} from "../modules/Audit";
import BetaBadge from "./BetaBadge";

export const AddEditServiceDialog = ({ open, onClose }) => {
    const theme = useTheme();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [allActiveLocations] = useAtom(allActiveLocationsAtom);
    const [allCategories] = useAtom(allCategoriesAtom);
    const [allServices, setAllServices] = useAtom(allServicesAtom);
    const [selectedService] = useAtom(selectedServiceAtom);
    const [, deleteServiceInAllServices] = useAtom(
        deleteServiceInAllServicesAtom
    );
    const [, updateServiceInAllServices] = useAtom(
        updateServiceInAllServicesAtom
    );
    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setConformationDialogIsSubmitting] = useAtom(
        conformationDialogIsSubmittingAtom
    );
    //Snackbar
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);

    // Add category on the fly dialog state
    const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
    const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
    const [openTaxSettings, setOpenTaxSettings] = useState(true);
    const [openSettings, setOpenSettings] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showCompletedDates, setShowCompletedDates] = useState(false);
    const [showCourseDatesModal, setShowCourseDatesModal] = useState(false);
    const coursesIsEnabled = companyCanUseCourses(mbxUser.company);

    const handleOpenAddCategoryDialog = () => setAddCategoryDialogOpen(true);
    const handleCloseAddCategoryDialog = () => setAddCategoryDialogOpen(false);

    const TAX_OPTIONS = [
        { label: "Automatic per region", value: "taxRate" },
        { label: "Apply custom tax percentage", value: "custom" },
        { label: "This service is tax exempt", value: "taxexempt" }
    ];

    const TAX_TOOLTIP =
        "In United States and Canada, the tax % is calculated automatically based on the state / province of the client";

    const parseTaxOverride = (val) => {
        if (val === null || val === undefined) return "";
        try {
            const parsed = JSON.parse(val);
            if (typeof parsed === "number" || typeof parsed === "string") {
                return String(parsed);
            }
        } catch (e) {}
        return String(val);
    };

    function getStartTimeAsNumber(startDateTime) {
        const [startDateTimePart] = startDateTime.split("|");
        const [date, time] = startDateTimePart.split("T");
        const [y, m, d] = date.split("-");
        const [hh, mm] = time.split(":");
        return new Date(y, m - 1, d, hh, mm, 0).getTime();
    }
    const {
        data: { courseSchedules: courseScheduleInfo, allowedMinimumCapacity },
        isLoading
    } = useQuery({
        queryKey: ["service" + selectedService?.id],
        queryFn: async () => {
            const result = await getJsonApi(
                "serviceManagement",
                `/service/${selectedService?.id}/courseschedule`
            );
            let courseSchedules = result.courseSchedules;
            let allowedMinimumCapacity =
                DEFAULT_ALLOWED_MINIMUM_COURSE_CAPACITY;
            courseSchedules.map((courseSchedule) => {
                courseSchedule.startDate = getBaseDate(
                    courseSchedule.startDate
                );
                courseSchedule.endDate = getBaseDate(courseSchedule.endDate);
                courseSchedule.rrule = getSettingsFromRrule(
                    courseSchedule.rrule
                );

                courseSchedule.timeBlocks = courseSchedule.assignment.map(
                    (assignment) => {
                        if (
                            !courseSchedule.pastCurrentDate &&
                            assignment.usedSeats !== assignment.maxSeats &&
                            allowedMinimumCapacity < assignment.usedSeats
                        )
                            allowedMinimumCapacity = assignment.usedSeats;
                        if (assignment.usedSeats > 0)
                            courseSchedule.disableRruleChange = true;
                        return {
                            startTime: getStartTimeAsNumber(
                                assignment.startDateTime
                            ),
                            providerInfo: assignment.providerInfo,
                            sequenceId: assignment.sequenceId,
                            usedSeats: assignment.usedSeats
                        };
                    }
                );
                return courseSchedule;
            });
            return { courseSchedules, allowedMinimumCapacity };
        },
        initialData: {
            courseSchedules: [],
            allowedMinimumCapacity: 0
        },
        enabled:
            !!selectedService?.id &&
            selectedService?.Classification === MB_SERVICE_CLASSIFICATION_GROUP
    });

    const mapServiceToFormValues = (service, courseScheduleInfo) => {
        const taxSetting = service?.taxexempt
            ? "taxexempt"
            : service?.TaxOverride && service.TaxOverride !== ""
              ? "custom"
              : "taxRate";
        let schedules = [];
        let capacity = DEFAULT_COURSE_CAPACITY;
        if (
            service?.Classification === MB_SERVICE_CLASSIFICATION_GROUP &&
            courseScheduleInfo
        ) {
            try {
                const settings = JSON.parse(service.CourseSettings);
                capacity = settings.capacity;
            } catch (e) {
                console.log("error parsing capacity");
                if (service.CourseSettings["capacity"])
                    capacity = service.CourseSettings.capacity;
            }
            schedules = courseScheduleInfo.map((schedule) => ({
                scheduleId: schedule.scheduleId,
                disableRruleChange: schedule.disableRruleChange,
                endDateHasPassed: schedule.endDate < new Date(),
                rruleStartDate: schedule.startDate
                    ? new Date(schedule.startDate)
                    : new Date(),
                rruleEndDate: schedule.endDate
                    ? new Date(schedule.endDate)
                    : addMonths(new Date(), 1),
                numberSelector: schedule.rrule
                    ? {
                          selectedDays: schedule.rrule.selectedDays || [],
                          selectedNumber: schedule.rrule.selectedNumber || 1
                      }
                    : { selectedDays: [], selectedNumber: 1 },
                rruleTimeBlocks:
                    schedule.timeBlocks && Array.isArray(schedule.timeBlocks)
                        ? schedule.timeBlocks
                        : [{ startTime: "" }]
            }));
        } else {
            schedules = [
                {
                    ...defaultRRule,
                    numberSelector: { selectedDays: [], selectedNumber: 1 },
                    scheduleId: 1
                }
            ];
        }

        let maxBookings = "";
        let scheduledBookings = 0;
        let exemptServiceFees = false;
        if (service?.Settings) {
            try {
                const parsedSettings =
                    typeof service.Settings === "string"
                        ? JSON.parse(service.Settings)
                        : service.Settings;
                if (parsedSettings?.maxBookings != null) {
                    maxBookings = String(parsedSettings.maxBookings);
                }
                if (parsedSettings?.scheduledBookings != null) {
                    scheduledBookings = parsedSettings.scheduledBookings;
                }
                if (parsedSettings?.exemptServiceFees != null) {
                    exemptServiceFees =
                        parsedSettings.exemptServiceFees !== false;
                }
            } catch (e) {
                console.log("error parsing Settings");
            }
        }

        return {
            serviceTypeId: service?.id,
            active: service?.active ?? true,
            serviceType:
                service?.Classification ?? MB_SERVICE_CLASSIFICATION_SIMPLE,
            serviceName: service?.name ?? "",
            categoryName: service?.categoryName ?? "",
            locations: service?.locations
                ? [...JSON.parse(service.locations)]
                : [],
            durationType: "Fixed",
            duration: service?.minutes ? String(service.minutes) : "60",
            allowRecurring: service?.allowRecurring ?? false,
            TaxOverride: parseTaxOverride(service?.TaxOverride),
            taxexempt: service?.taxexempt ?? false,
            bookingBehaviour: service?.behavior ?? "EXTERNAL",
            price: service?.price ?? 0,
            description: service?.desc ?? "",
            taxSetting,
            capacity: capacity,
            schedules,
            maxBookings,
            scheduledBookings,
            exemptServiceFees
        };
    };

    const isEditing = Boolean(selectedService && selectedService.id);
    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        getValues,
        formState: { errors, isValid, dirtyFields, isSubmitted }
    } = useForm({
        mode: "onChange",
        defaultValues: mapServiceToFormValues(
            selectedService,
            courseScheduleInfo
        )
    });
    useEffect(() => {
        if (courseScheduleInfo) {
            reset(mapServiceToFormValues(selectedService, courseScheduleInfo));
        }
    }, [courseScheduleInfo, reset]);
    const {
        fields: scheduleFields,
        append: appendSchedule,
        remove: removeSchedule
    } = useFieldArray({ control, name: "schedules" });

    const watchServiceType = watch("serviceType");
    const watchTaxSetting = watch("taxSetting");
    const watchLocations = watch("locations");
    const watchSchedules = watch("schedules");
    const activeValue = watch("active");

    function hasFutureBookings() {
        return watchSchedules &&
            watchServiceType === MB_SERVICE_CLASSIFICATION_GROUP
            ? watchSchedules?.some(
                  (schedule) =>
                      !schedule.endDateHasPassed && schedule.disableRruleChange
              )
            : false;
    }

    function allCoursesCompleted() {
        if (!Array.isArray(watchSchedules) || watchSchedules?.length === 0) {
            return false;
        }
        return watchSchedules.every((schedule) => schedule.endDateHasPassed);
    }
    // Locations validation
    const isLocationsValid = (locations) => {
        if (!Array.isArray(locations)) return false;
        return locations.length > 0;
    };

    // For GROUP services: validate schedules and time blocks
    const isGroupSchedulesValid = (schedules) => {
        if (!Array.isArray(schedules) || schedules.length === 0) {
            return false;
        }
        for (let i = 0; i < schedules.length; i++) {
            const schedule = schedules[i];
            if (!schedule.rruleStartDate) {
                return false;
            }
            if (
                !Array.isArray(schedule.rruleTimeBlocks) ||
                schedule.rruleTimeBlocks.length === 0
            ) {
                return false;
            }
            let foundInValidBlock = false;
            for (let tb of schedule.rruleTimeBlocks) {
                const hasStartTime =
                    tb.startTime !== undefined &&
                    tb.startTime !== "" &&
                    tb.startTime !== null;
                const hasProvider =
                    tb.providerInfo !== undefined &&
                    tb.providerInfo !== null &&
                    tb.providerInfo?.id;
                if (!hasStartTime || !hasProvider) {
                    foundInValidBlock = true;
                    break;
                }
            }
            if (foundInValidBlock) {
                return false;
            }
        }
        return true;
    };

    const numericInputProps = {
        inputProps: { inputMode: "numeric", pattern: "[0-9]*", min: 1 },
        onInput: (e) => {
            e.target.value = e.target.value.replace(/[^0-9]/g, "");
        }
    };

    const handleAddRuleSection = () => {
        appendSchedule({
            ...defaultRRule,
            numberSelector: { selectedDays: [], selectedNumber: 1 },
            scheduleId: watchSchedules?.length ? watchSchedules.length + 1 : 1
        });
    };
    const handleActivenessChangeOfService = async (changedActiveValue) => {
        setConformationDialogIsSubmitting(true);
        try {
            const categoryName = getValues("categoryName");
            const selectedCategory = allCategories.find(
                (cat) => cat.name === categoryName
            );
            let servicePayload = {
                companyId: mbxUser.companyId,
                name: getValues("serviceName"),
                categoryName: getValues("categoryName"),
                categoryId: selectedCategory?.id,
                active: changedActiveValue
            };
            const { service: changedService } = await putApi(
                "serviceManagement",
                `/service/${selectedService.id}`,
                { body: servicePayload }
            );
            if (changedService) {
                await auditServiceUpdate(mbxUser, changedService, {
                    oldName: selectedService.name,
                    ...changedService
                });
                setUserPageSnackbar({
                    open: true,
                    message: "Service has been successfully updated",
                    severity: "success"
                });
                updateServiceInAllServices({
                    id: selectedService.id,
                    active: changedActiveValue
                });
                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                onClose();
            } else {
                setUserPageSnackbar({
                    open: true,
                    message: `Unexpected error while deleteing service.`,
                    severity: "error"
                });

                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                throw new Error(`Error deleting service.`);
            }
        } catch (error) {
            console.log("Error deleting service:", error);
            setConformationDialogIsSubmitting(false);
            setConfirmationDialogOpen(false);
        }
    };
    const handleDeleteService = async () => {
        setConformationDialogIsSubmitting(true);
        try {
            const categoryName = getValues("categoryName");
            const selectedCategory = allCategories.find(
                (cat) => cat.name === categoryName
            );
            const { service: deletedService } = await postApi(
                "serviceManagement",
                "/service",
                {
                    body: {
                        deleteServiceInfo: {
                            serviceTypeId: selectedService.id
                        },
                        name: getValues("serviceName"),
                        category: {
                            id: selectedCategory?.id,
                            categoryName: getValues("categoryName")
                        }
                    }
                }
            );
            if (deletedService) {
                await auditServiceDelete(mbxUser, deletedService);
                setUserPageSnackbar({
                    open: true,
                    message: "Service has been successfully deleted",
                    severity: "success"
                });
                deleteServiceInAllServices(selectedService.id);
                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                onClose();
            } else {
                setUserPageSnackbar({
                    open: true,
                    message: `Unexpected error while deleteing service.`,
                    severity: "error"
                });
                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                throw new Error(`Error deleting service.`);
            }
        } catch (error) {
            console.log("Error deleting service:", error);
            setConformationDialogIsSubmitting(false);
            setConfirmationDialogOpen(false);
        }
    };

    const handleDeleteDateRange = async (scheduleId, sectionIdx) => {
        /*
        case 1- delete a date range when creating new course
        case 2- delete an existing date range( i.e. saved in the db) of existing course
        case 3- delete a new date range added ( i.e. NO saved in the db yet)  on a an existing course
        */
        setConformationDialogIsSubmitting(true);

        try {
            const scheduleIndex = courseScheduleInfo.findIndex(
                (schedule) => schedule.scheduleId === scheduleId
            );
            if (scheduleIndex === -1) {
                //means this date-range is not stored in the db yet - case 1, case 3
                removeSchedule(sectionIdx);
            } else {
                // case 2
                const diff = createScheduleDiffObjectForDateRangeDeletion(
                    courseScheduleInfo,
                    scheduleId
                );
                const selectedCategory = allCategories.find(
                    (cat) => cat.name === getValues("categoryName")
                );

                let servicePayload = {
                    companyId: mbxUser.companyId,
                    name: getValues("serviceName"),
                    categoryName: getValues("categoryName"),
                    categoryId: selectedCategory?.id,
                    courseScheduleChanges: diff
                };
                const response = await putApi(
                    "serviceManagement",
                    `/service/${selectedService.id}`,
                    { body: servicePayload }
                );
                if (response.service) {
                    await auditServiceUpdate(mbxUser, response.service, {
                        oldName: selectedService.name,
                        ...response.service
                    });
                    setUserPageSnackbar({
                        open: true,
                        message: "Date range has been successfully deleted",
                        severity: "success"
                    });
                    const scheduleIndex = courseScheduleInfo.findIndex(
                        (schedule) => schedule.scheduleId === scheduleId
                    );
                    if (scheduleIndex)
                        courseScheduleInfo.splice(scheduleIndex, 1);
                    removeSchedule(sectionIdx);
                }
            }

            setConformationDialogIsSubmitting(false);
            setConfirmationDialogOpen(false);
        } catch (e) {
            console.log("Error deleting date range:", e);
            setConformationDialogIsSubmitting(false);
            setConfirmationDialogOpen(false);
        }
    };
    const onSubmit = async (data) => {
        setIsSaving(true);
        try {
            //data.description check
            const dirtyValues = getDirtyValues(dirtyFields, data);
            if (data.taxSetting === "custom") {
                if (!data.TaxOverride || data.TaxOverride === "") {
                    data.taxSetting = "taxRate";
                    data.TaxOverride = null;
                    data.taxexempt = false;
                }
            }

            const taxSetting = data.taxSetting;
            if (taxSetting === "taxRate") {
                data.TaxOverride = null;
                data.taxexempt = false;
            } else if (taxSetting === "custom") {
                data.taxexempt = false;
            } else if (taxSetting === "taxexempt") {
                data.TaxOverride = null;
                data.taxexempt = true;
            }

            const serviceTypeFieldMap = {
                active: "active",
                serviceType: "Classification",
                serviceName: "name",
                categoryName: "categoryName",
                locations: "locations",
                durationType: "durationType",
                duration: "minutes",
                allowRecurring: "allowRecurring",
                TaxOverride: "TaxOverride",
                taxexempt: "taxexempt",
                bookingBehaviour: "behavior",
                price: "price",
                description: "desc"
            };

            const selectedCategory = allCategories.find(
                (cat) => cat.name === data.categoryName
            );
            let servicePayload = {
                companyId: mbxUser.companyId,
                name: data.serviceName,
                categoryName: data.categoryName,
                categoryId: selectedCategory?.id,
                TaxOverride: data.TaxOverride,
                taxexempt: data.taxexempt
            };
            let serviceHasEdits = false;
            if (isEditing)
                Object.keys(serviceTypeFieldMap).forEach((serviceKey) => {
                    if (dirtyFields[serviceKey]) {
                        if (isEditing) serviceHasEdits = true;
                        if (serviceKey === "categoryName") {
                            const selectedCategory = allCategories.find(
                                (cat) => cat.name === data.categoryName
                            );
                            if (selectedCategory) {
                                servicePayload["categoryId"] =
                                    selectedCategory.id;
                                servicePayload["categoryName"] =
                                    selectedCategory.name;
                            }
                        } else if (serviceKey === "price") {
                            servicePayload["price"] =
                                typeof data.price === "number" &&
                                !isNaN(data.price)
                                    ? data.price
                                    : parseFloat(data.price.replace(/,/g, ""));
                        } else
                            servicePayload[serviceTypeFieldMap[serviceKey]] =
                                data[serviceKey];
                    }
                });
            // Handle Settings (maxBookings) for SIMPLE services on edit
            if (
                isEditing &&
                data.serviceType === MB_SERVICE_CLASSIFICATION_SIMPLE &&
                dirtyFields.maxBookings
            ) {
                if (data.maxBookings && data.maxBookings !== "") {
                    let existingSettings = {};
                    if (selectedService?.Settings) {
                        try {
                            existingSettings =
                                typeof selectedService.Settings === "string"
                                    ? JSON.parse(selectedService.Settings)
                                    : selectedService.Settings;
                        } catch (e) {}
                    }
                    servicePayload.Settings = JSON.stringify({
                        ...existingSettings,
                        maxBookings: Number(data.maxBookings),
                        exemptServiceFees: data.exemptServiceFees
                    });
                } else {
                    servicePayload.Settings = null;
                }
                serviceHasEdits = true;
            }
            if (isEditing && dirtyFields.exemptServiceFees) {
                if (typeof servicePayload.Settings === "string") {
                    servicePayload.Settings = JSON.stringify({
                        ...JSON.parse(servicePayload.Settings),
                        exemptServiceFees: data.exemptServiceFees
                    });
                } else {
                    let existingSettings = {};
                    if (selectedService?.Settings) {
                        try {
                            existingSettings =
                                typeof selectedService.Settings === "string"
                                    ? JSON.parse(selectedService.Settings)
                                    : selectedService.Settings;
                        } catch (e) {}
                    }
                    servicePayload.Settings = JSON.stringify({
                        ...existingSettings,
                        exemptServiceFees: data.exemptServiceFees
                    });
                }
            }
            // if only an assignment is changed, we need to delete and recreate CourseSession records of the changed assignment.
            // if anything other than assignment is changed, we need to delete and recreate CourseSchedule records and CourseSession records
            if (isEditing && dirtyFields.schedules && dirtyValues.schedules) {
                const courseScheduleChanges = getCourseSchedulesDiff(
                    courseScheduleInfo,
                    dirtyValues.schedules
                );

                if (courseScheduleChanges.changedSchedules) {
                    courseScheduleChanges.changedSchedules.forEach(
                        (changedSchedule) => {
                            if (
                                changedSchedule.assignmentChanges &&
                                changedSchedule.assignmentChanges.changes
                                    ?.length
                            ) {
                                changedSchedule.assignmentChanges.changes.forEach(
                                    (assignment) => {
                                        if (assignment.originalItem)
                                            assignment.originalItem.startTime =
                                                timestampToTimeString(
                                                    assignment.originalItem
                                                        .startTime
                                                );
                                        if (assignment.newItem)
                                            assignment.newItem.startTime =
                                                timestampToTimeString(
                                                    assignment.newItem.startTime
                                                );
                                        if (assignment.item)
                                            assignment.item.startTime =
                                                timestampToTimeString(
                                                    assignment.item.startTime
                                                );
                                    }
                                );
                            }
                        }
                    );
                }
                servicePayload.courseScheduleChanges = courseScheduleChanges;
            }

            if (!isEditing) {
                const selectedCategory = allCategories.find(
                    (cat) => cat.name === data.categoryName
                );
                servicePayload = {
                    companyId: mbxUser.companyId,
                    name: data.serviceName,
                    desc: data.description,
                    minutes: data.duration,
                    behavior: data.bookingBehaviour,
                    price:
                        typeof data.price === "number" && !isNaN(data.price)
                            ? data.price
                            : parseFloat(data.price.replace(/,/g, "")),
                    taxexempt: data.taxexempt,
                    TaxOverride: data.TaxOverride,
                    allowRecurring: data.allowRecurring,
                    Classification: data.serviceType,
                    locations: data.locations,
                    categoryName: data.categoryName,
                    categoryId: selectedCategory?.id,
                    category: selectedCategory
                };
                // Add Settings for new SIMPLE services with maxBookings
                if (
                    data.serviceType === MB_SERVICE_CLASSIFICATION_SIMPLE &&
                    data.maxBookings &&
                    data.maxBookings !== ""
                ) {
                    servicePayload.Settings = JSON.stringify({
                        maxBookings: Number(data.maxBookings),
                        scheduledBookings: 0
                    });
                }
                if (typeof servicePayload.Settings === "string") {
                    servicePayload.Settings = JSON.stringify({
                        ...JSON.parse(servicePayload.Settings),
                        exemptServiceFees: data.exemptServiceFees
                    });
                } else {
                    servicePayload.Settings = JSON.stringify({
                        exemptServiceFees: data.exemptServiceFees
                    });
                }
            }
            if (
                data.serviceType === MB_SERVICE_CLASSIFICATION_SIMPLE &&
                dirtyFields.serviceType &&
                isEditing
            ) {
                //catch the case when service type is changed from COURSE to SIMPLE.
                //reset capacity to 1
                servicePayload.CourseSettings = { capacity: 1 };
            }
            if (
                data.serviceType === MB_SERVICE_CLASSIFICATION_GROUP &&
                dirtyFields.serviceType &&
                isEditing
            ) {
                //classification switched from simple to group but the capacity field is not dirty, then set capacity to default.
                servicePayload.CourseSettings = {
                    capacity: DEFAULT_COURSE_CAPACITY
                };
                // Clear Settings when switching from SIMPLE to GROUP
                servicePayload.Settings = null;
            }
            if (data.serviceType === MB_SERVICE_CLASSIFICATION_GROUP) {
                if (isEditing) {
                    if (dirtyFields.capacity)
                        servicePayload.CourseSettings = {
                            capacity: Number(data.capacity)
                        };
                } else
                    servicePayload.CourseSettings = {
                        capacity: Number(data.capacity)
                    };
                // Get the timezone for the selected location
                let locationTimezone = "";
                if (
                    Array.isArray(data.locations) &&
                    data.locations.length > 0
                ) {
                    const locObj = allActiveLocations.find(
                        (loc) => loc.id === data.locations[0]
                    );
                    locationTimezone = locObj?.timezone || "";
                }
                servicePayload.locationTimezone = locationTimezone;

                if (!isEditing)
                    servicePayload.courseSchedules =
                        Array.isArray(data.schedules) && data.schedules.length
                            ? data.schedules.map((schedule) => {
                                  const selectedDays =
                                      schedule.numberSelector?.selectedDays ||
                                      [];
                                  const selectedNumber =
                                      schedule.numberSelector?.selectedNumber ||
                                      1;
                                  const rruleString = convertToRRuleString(
                                      selectedDays,
                                      selectedNumber
                                  );

                                  // assignment array: one object for each rruleTimeBlock, containing time and providerId
                                  const assignment = Array.isArray(
                                      schedule.rruleTimeBlocks
                                  )
                                      ? schedule.rruleTimeBlocks.map((tb) => ({
                                            startTime: timestampToTimeString(
                                                tb.startTime
                                            ),
                                            providerId:
                                                tb.providerInfo?.id || ""
                                        }))
                                      : [];

                                  return {
                                      startDate: getYYYYMMDD(
                                          schedule.rruleStartDate
                                      ),
                                      endDate: getYYYYMMDD(
                                          schedule.rruleEndDate
                                      ),
                                      rrule: rruleString,
                                      assignment
                                      //   ...(locationTimezone
                                      //       ? { locationTimezone }
                                      //       : {})
                                  };
                              })
                            : [];
            }

            let response;
            if (isEditing) {
                response = await putApi(
                    "serviceManagement",
                    `/service/${selectedService.id}`,
                    { body: servicePayload }
                );
            } else {
                response = await postApi("serviceManagement", "/service", {
                    body: servicePayload
                });
            }
            if (response?.service && response?.service?.id) {
                setUserPageSnackbar({
                    open: true,
                    message: isEditing
                        ? "Service has been successfully updated"
                        : "Service has been successfully created",
                    severity: "success"
                });
                if (isEditing) {
                    // update service in allServices, merging Settings from payload
                    // in case the API response doesn't include it
                    const updatedService = {
                        ...response.service,
                        ...(servicePayload.Settings !== undefined &&
                        !response.service.Settings
                            ? { Settings: servicePayload.Settings }
                            : {})
                    };
                    setAllServices(
                        allServices.map((service) =>
                            service.id === response.service.id
                                ? updatedService
                                : service
                        )
                    );

                    await auditServiceUpdate(mbxUser, response.service, {
                        oldName: selectedService.name,
                        ...response.service
                    });
                } else {
                    await auditServiceCreate(mbxUser, response.service);
                    const newService = {
                        ...response.service,
                        ...(servicePayload.Settings !== undefined &&
                        !response.service.Settings
                            ? { Settings: servicePayload.Settings }
                            : {})
                    };
                    setAllServices([newService, ...allServices]);
                }
                handleClose();
                return response;
            } else {
                console.log("response does not have service");
                handleClose();
                return null;
            }
        } catch (e) {
            console.log(e);
            handleClose();
            return null;
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        reset(mapServiceToFormValues(selectedService));
        onClose();
    };

    function validatePrice() {
        const priceValue = watch("price");
        if (priceValue === 0) return true;
        if (!priceValue) return false;
        return true;
    }
    // Save button enabled/disabled logic
    const requiredFieldsValid = () => {
        // Validate base required fields
        if (
            !watch("serviceName") ||
            !watch("serviceName").trim() ||
            !watch("categoryName") ||
            !watch("duration") ||
            !validatePrice()
        ) {
            return false;
        }
        // Locations must be at least one selected
        if (
            watchServiceType === MB_SERVICE_CLASSIFICATION_GROUP &&
            !isLocationsValid(watchLocations)
        ) {
            return false;
        }
        // For GROUP, validate schedules and capacity
        if (watchServiceType === MB_SERVICE_CLASSIFICATION_GROUP) {
            if (!isGroupSchedulesValid(watchSchedules)) {
                return false;
            }
            if (
                !watch("capacity") ||
                !Number.isInteger(Number(watch("capacity"))) ||
                Number(watch("capacity")) <= 0
            ) {
                return false;
            }
        }
        // For custom tax, validate custom tax value
        if (watchTaxSetting === "custom") {
            const taxVal = watch("TaxOverride");
            if (
                !taxVal ||
                !/^[0-9]+(\.[0-9]{1,2})?$/.test(taxVal) ||
                Number(taxVal) < 0 ||
                Number(taxVal) > 100
            ) {
                return false;
            }
        }

        return true;
    };
    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <ValidateCourseDatesModal
                open={showCourseDatesModal}
                getValues={getValues}
                close={() => setShowCourseDatesModal(false)}
            />
            <Dialog
                maxWidth={"md"}
                open={open}
                onClose={onClose}
                aria-labelledby="-edit-service-dialog-title"
            >
                <DialogTitle
                    id="add-edit-service-dialog-title"
                    sx={{ paddingBottom: "18px !important" }}
                >
                    {selectedService
                        ? `${selectedService.name}`
                        : "Add service"}
                </DialogTitle>
                <form
                    key={selectedService?.id || "newService"}
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%"
                    }}
                >
                    <DialogContent>
                        {selectedService?.Classification ===
                            MB_SERVICE_CLASSIFICATION_GROUP && isLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center"
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Grid container item spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "18px"
                                            }}
                                        >
                                            <FormControl fullWidth>
                                                <InputLabel
                                                    size="small"
                                                    disabled={
                                                        !coursesIsEnabled ||
                                                        hasFutureBookings()
                                                    }
                                                >
                                                    Service type
                                                </InputLabel>
                                                <Controller
                                                    name="serviceType"
                                                    control={control}
                                                    render={({
                                                        field: {
                                                            onChange:
                                                                onChangeFormHook,
                                                            value
                                                        }
                                                    }) => (
                                                        <Select
                                                            id="serviceType"
                                                            name="serviceType"
                                                            labelId="serviceType-label"
                                                            label="ServiceType"
                                                            sx={{
                                                                "& fieldset": {
                                                                    "& legend":
                                                                        {
                                                                            width: "65px"
                                                                        }
                                                                }
                                                            }}
                                                            value={value}
                                                            onChange={(e) => {
                                                                onChangeFormHook(
                                                                    e.target
                                                                        .value
                                                                );
                                                            }}
                                                            disabled={
                                                                !coursesIsEnabled ||
                                                                hasFutureBookings()
                                                            }
                                                        >
                                                            <MenuItem
                                                                value={
                                                                    MB_SERVICE_CLASSIFICATION_SIMPLE
                                                                }
                                                            >
                                                                One-on-one
                                                            </MenuItem>
                                                            <MenuItem
                                                                value={
                                                                    MB_SERVICE_CLASSIFICATION_GROUP
                                                                }
                                                            >
                                                                Group
                                                            </MenuItem>
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item sx={{ marginLeft: "-4px" }}>
                                        <InfoTooltip
                                            title={`One-to-one services have no set dates and are great for simple home repairs,          installations, auto-detailing, audits, etc \n\nGroup services have fixed start and end dates and are great for swim schools, dance or fitness studios, and virtual learning platforms`}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item spacing={2}>
                                    <Grid item xs={12} sm={12}>
                                        <Controller
                                            name="serviceName"
                                            control={control}
                                            rules={{
                                                required:
                                                    "Please enter a service name",
                                                validate: (value) => {
                                                    if (!value.trim())
                                                        return "Please enter a service name";
                                                    return true;
                                                }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    label="Service name *"
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    helperText={
                                                        fieldState.error
                                                            ?.message || ""
                                                    }
                                                    InputLabelProps={{
                                                        shrink: true
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel
                                                id="category-label"
                                                size="small"
                                                shrink
                                            >
                                                Category *
                                            </InputLabel>
                                            <Controller
                                                name="categoryName"
                                                control={control}
                                                rules={{
                                                    required:
                                                        "Please select a category"
                                                }}
                                                render={({
                                                    field: {
                                                        onChange:
                                                            onChangeFormHook,
                                                        value: categoryName
                                                    }
                                                }) => (
                                                    <>
                                                        <Select
                                                            labelId="category-label"
                                                            id="categoryName"
                                                            name="categoryName"
                                                            label="category"
                                                            value={categoryName}
                                                            onChange={(e) => {
                                                                onChangeFormHook(
                                                                    e.target
                                                                        .value
                                                                );
                                                            }}
                                                            notched
                                                        >
                                                            {allCategories.map(
                                                                (category) => (
                                                                    <MenuItem
                                                                        value={
                                                                            category.name
                                                                        }
                                                                        key={
                                                                            category.name
                                                                        }
                                                                    >
                                                                        <Tooltip
                                                                            title={
                                                                                category
                                                                                    .name
                                                                                    .length >
                                                                                80
                                                                                    ? category.name
                                                                                    : ""
                                                                            }
                                                                            placement="right"
                                                                        >
                                                                            <Typography
                                                                                noWrap
                                                                                sx={{
                                                                                    overflow:
                                                                                        "hidden",
                                                                                    textOverflow:
                                                                                        "ellipsis",
                                                                                    whiteSpace:
                                                                                        "nowrap",
                                                                                    width: "100%",
                                                                                    cursor: "pointer"
                                                                                }}
                                                                            >
                                                                                {category
                                                                                    .name
                                                                                    .length >
                                                                                80
                                                                                    ? `${category.name.substring(
                                                                                          0,
                                                                                          80
                                                                                      )}...`
                                                                                    : category.name}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                            <Grid
                                                                item
                                                                variant={
                                                                    "dropdownDivider"
                                                                }
                                                            >
                                                                <Divider />
                                                            </Grid>
                                                            <MenuItem
                                                                onClick={() =>
                                                                    handleOpenAddCategoryDialog()
                                                                }
                                                                sx={{
                                                                    fontWeight:
                                                                        "bold",
                                                                    color: theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                                    "& .MuiListItemIcon-root":
                                                                        {
                                                                            minWidth:
                                                                                "32px"
                                                                        }
                                                                }}
                                                                disableRipple
                                                                disableGutters
                                                            >
                                                                <ListItemIcon>
                                                                    <AddIcon
                                                                        fontSize="small"
                                                                        sx={{
                                                                            color: theme
                                                                                .palette
                                                                                .primary
                                                                                .main
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight:
                                                                            "bold",
                                                                        color: theme
                                                                            .palette
                                                                            .primary
                                                                            .main
                                                                    }}
                                                                >
                                                                    Add category
                                                                </Typography>
                                                            </MenuItem>
                                                        </Select>
                                                        <AddCategoriesOnTheFlyDialog
                                                            open={
                                                                addCategoryDialogOpen
                                                            }
                                                            onClose={
                                                                handleCloseAddCategoryDialog
                                                            }
                                                            onCategoryCreated={(
                                                                cat
                                                            ) => {
                                                                setValue(
                                                                    "categoryName",
                                                                    cat.name,
                                                                    {
                                                                        shouldValidate: true
                                                                    }
                                                                );
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel size="small">
                                                Booking type
                                            </InputLabel>
                                            <Controller
                                                name="bookingBehaviour"
                                                control={control}
                                                render={({
                                                    field: {
                                                        onChange:
                                                            onChangeFormHook,
                                                        value
                                                    }
                                                }) => (
                                                    <Select
                                                        id="bookingBehaviour"
                                                        name="bookingBehaviour"
                                                        labelId="booking-behaviour"
                                                        label="Booking type"
                                                        sx={{
                                                            "& fieldset": {
                                                                "& legend": {
                                                                    width: "72px"
                                                                }
                                                            },
                                                            ".MuiSelect-select":
                                                                {
                                                                    paddingRight:
                                                                        "20px !important"
                                                                }
                                                        }}
                                                        value={value}
                                                        onChange={(e) => {
                                                            onChangeFormHook(
                                                                e.target.value
                                                            );
                                                        }}
                                                    >
                                                        <MenuItem
                                                            value={"EXTERNAL"}
                                                        >
                                                            Bookable by clients
                                                            and admins
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={"INTERNAL"}
                                                        >
                                                            Bookable by admins
                                                            only
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={"MANUAL"}
                                                        >
                                                            Waitlist only
                                                        </MenuItem>
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                <Grid container item spacing={2}>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="description"
                                            control={control}
                                            rules={{
                                                required:
                                                    "Description is required"
                                            }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label="Description (public) *"
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    onFocus={() =>
                                                        setIsDescriptionFocused(
                                                            true
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        setIsDescriptionFocused(
                                                            false
                                                        )
                                                    }
                                                    InputLabelProps={{
                                                        shrink: true
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root":
                                                            {
                                                                height: 100,
                                                                minHeight: 100,
                                                                maxHeight: 100,
                                                                overflow:
                                                                    "hidden",
                                                                "& .MuiOutlinedInput-notchedOutline":
                                                                    {
                                                                        borderTopWidth:
                                                                            isDescriptionFocused
                                                                                ? "3px"
                                                                                : "1px"
                                                                    }
                                                            },
                                                        "& .MuiOutlinedInput-input":
                                                            {
                                                                paddingTop:
                                                                    "20px"
                                                            }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item spacing={2}>
                                    <Grid item xs={12} sm={12}>
                                        <Tooltip
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        width: "200px"
                                                    }
                                                }
                                            }}
                                            disableInteractive
                                            title={
                                                hasFutureBookings() &&
                                                "There are already live bookings on at least one start date of this course"
                                            }
                                        >
                                            <FormControl
                                                fullWidth
                                                sx={{ gap: 0.5 }}
                                            >
                                                <InputLabel
                                                    id="locations-label"
                                                    size="small"
                                                    disabled={hasFutureBookings()}
                                                >
                                                    Location
                                                    {watchServiceType ===
                                                    MB_SERVICE_CLASSIFICATION_GROUP
                                                        ? ""
                                                        : "s"}
                                                </InputLabel>
                                                <Controller
                                                    name="locations"
                                                    control={control}
                                                    render={({
                                                        field: {
                                                            onChange:
                                                                onChangeFormHook,
                                                            value: locations
                                                        }
                                                    }) => (
                                                        <Select
                                                            disabled={hasFutureBookings()}
                                                            labelId="locations-label"
                                                            id="locations"
                                                            name="locations"
                                                            label={
                                                                watchServiceType ===
                                                                MB_SERVICE_CLASSIFICATION_GROUP
                                                                    ? "Location"
                                                                    : "Locations"
                                                            }
                                                            multiple={
                                                                watchServiceType !==
                                                                MB_SERVICE_CLASSIFICATION_GROUP
                                                            }
                                                            value={locations}
                                                            sx={{
                                                                "& fieldset": {
                                                                    "& legend":
                                                                        {
                                                                            width: "55px"
                                                                        }
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                if (
                                                                    watchServiceType ===
                                                                    MB_SERVICE_CLASSIFICATION_GROUP
                                                                ) {
                                                                    onChangeFormHook(
                                                                        [
                                                                            e
                                                                                .target
                                                                                .value
                                                                        ]
                                                                    );
                                                                } else {
                                                                    onChangeFormHook(
                                                                        e.target
                                                                            .value
                                                                    );
                                                                }
                                                            }}
                                                            renderValue={() =>
                                                                watchServiceType ===
                                                                MB_SERVICE_CLASSIFICATION_GROUP
                                                                    ? allActiveLocations.find(
                                                                          (l) =>
                                                                              l.id ===
                                                                              locations[0]
                                                                      )
                                                                          ?.locationname ||
                                                                      ""
                                                                    : locations.length ===
                                                                        allActiveLocations.length
                                                                      ? "All locations"
                                                                      : allActiveLocations
                                                                            .filter(
                                                                                (
                                                                                    location
                                                                                ) =>
                                                                                    locations.includes(
                                                                                        location.id
                                                                                    )
                                                                            )
                                                                            .map(
                                                                                (
                                                                                    location
                                                                                ) =>
                                                                                    location.locationname
                                                                            )
                                                                            .join(
                                                                                ", "
                                                                            )
                                                            }
                                                        >
                                                            {allActiveLocations.map(
                                                                (location) => (
                                                                    <MenuItem
                                                                        value={
                                                                            location.id
                                                                        }
                                                                        key={
                                                                            location.id
                                                                        }
                                                                    >
                                                                        {watchServiceType !==
                                                                            MB_SERVICE_CLASSIFICATION_GROUP && (
                                                                            <Checkbox
                                                                                checked={locations.includes(
                                                                                    location.id
                                                                                )}
                                                                            />
                                                                        )}
                                                                        {
                                                                            location.locationname
                                                                        }
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                        </Tooltip>
                                    </Grid>
                                </Grid>

                                <Grid
                                    container
                                    item
                                    spacing={2}
                                    alignItems="flex-start"
                                >
                                    <Grid item xs={6} sm={3}>
                                        <Controller
                                            name="price"
                                            control={control}
                                            rules={{
                                                required: "Please enter a price"
                                            }}
                                            render={({ field }) => (
                                                <CurrencyFormat
                                                    {...field}
                                                    customInput={TextField}
                                                    label="Price"
                                                    size="small"
                                                    fullWidth
                                                    thousandSeparator={true}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment
                                                                position="start"
                                                                disableTypography
                                                            >
                                                                $
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    {watchServiceType ===
                                        MB_SERVICE_CLASSIFICATION_GROUP && (
                                        <Grid item xs={6} sm={3}>
                                            <Controller
                                                name="capacity"
                                                control={control}
                                                rules={{
                                                    required:
                                                        "Please enter capacity",
                                                    validate: (value) => {
                                                        if (
                                                            value &&
                                                            Number.isInteger(
                                                                Number(value)
                                                            )
                                                        ) {
                                                            return allowedMinimumCapacity
                                                                ? value >=
                                                                  allowedMinimumCapacity
                                                                    ? true
                                                                    : `Capacity must be greater than or equal to ${allowedMinimumCapacity}`
                                                                : true;
                                                        } else
                                                            return "Must be a positive integer";
                                                    }
                                                }}
                                                render={({
                                                    field,
                                                    fieldState
                                                }) => (
                                                    <TextField
                                                        {...field}
                                                        label="Capacity"
                                                        size="small"
                                                        type="text"
                                                        fullWidth
                                                        {...numericInputProps}
                                                        error={
                                                            !!fieldState.error
                                                        }
                                                        helperText={
                                                            fieldState.error
                                                                ?.message || ""
                                                        }
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid container item spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                        <Controller
                                            name="duration"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label="Duration *"
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment
                                                                position="end"
                                                                disableTypography
                                                            >
                                                                mins
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item spacing={2}>
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            display: "flex",
                                            height: "50px",
                                            alignItems: "center"
                                        }}
                                    >
                                        <Button
                                            name="tax-settings-btn"
                                            disabled={isSaving}
                                            onClick={() =>
                                                setOpenTaxSettings(
                                                    (open) => !open
                                                )
                                            }
                                            variant="text"
                                            color="primary"
                                            disableRipple
                                            endIcon={
                                                openTaxSettings ? (
                                                    <ExpandLess />
                                                ) : (
                                                    <ExpandMore />
                                                )
                                            }
                                            sx={{ textTransform: "none" }}
                                        >
                                            Tax settings
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Collapse in={openTaxSettings}>
                                    <Grid container item spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel
                                                    id="tax-setting-label"
                                                    shrink
                                                >
                                                    Tax
                                                </InputLabel>
                                                <Controller
                                                    name="taxSetting"
                                                    control={control}
                                                    defaultValue="taxRate"
                                                    render={({ field }) => (
                                                        <Select
                                                            {...field}
                                                            labelId="tax-setting-label"
                                                            label="Tax"
                                                            sx={{
                                                                ".MuiSelect-select":
                                                                    {
                                                                        paddingRight:
                                                                            "10px !important"
                                                                    }
                                                            }}
                                                            value={field.value}
                                                        >
                                                            {TAX_OPTIONS.map(
                                                                (option) => (
                                                                    <MenuItem
                                                                        value={
                                                                            option.value
                                                                        }
                                                                        key={
                                                                            option.value
                                                                        }
                                                                    >
                                                                        {
                                                                            option.label
                                                                        }
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid>
                                        {watchTaxSetting === "custom" && (
                                            <Grid item xs={12} sm={3}>
                                                <Controller
                                                    name="TaxOverride"
                                                    control={control}
                                                    rules={{
                                                        pattern: {
                                                            value: /^[0-9]+(\.[0-9]{1,2})?$/,
                                                            message:
                                                                "Enter a valid number"
                                                        },
                                                        min: {
                                                            value: 0,
                                                            message:
                                                                "Must be 0 or more"
                                                        },
                                                        max: {
                                                            value: 100,
                                                            message:
                                                                "Cannot exceed 100%"
                                                        }
                                                    }}
                                                    render={({
                                                        field,
                                                        fieldState
                                                    }) => (
                                                        <TextField
                                                            {...field}
                                                            label="Custom tax *"
                                                            size="small"
                                                            error={
                                                                !!fieldState.error
                                                            }
                                                            helperText={
                                                                fieldState.error
                                                                    ?.message
                                                            }
                                                            In
                                                            InputLabelProps={{
                                                                shrink: true
                                                            }}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        %
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        )}
                                        <Box
                                            sx={{
                                                paddingTop: "15px",
                                                paddingLeft: "10px",
                                                display: "flex",
                                                alignItems: "center"
                                            }}
                                        >
                                            <InfoTooltip
                                                title={TAX_TOOLTIP}
                                            ></InfoTooltip>
                                        </Box>
                                    </Grid>
                                </Collapse>
                                <Grid container item spacing={2}>
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            display: "flex",
                                            height: "50px",
                                            alignItems: "center"
                                        }}
                                    >
                                        <Button
                                            name="settings-btn"
                                            disabled={isSaving}
                                            onClick={() =>
                                                setOpenSettings((open) => !open)
                                            }
                                            variant="text"
                                            color="primary"
                                            disableRipple
                                            endIcon={
                                                openSettings ? (
                                                    <ExpandLess />
                                                ) : (
                                                    <ExpandMore />
                                                )
                                            }
                                            sx={{
                                                textTransform: "none"
                                            }}
                                        >
                                            Settings
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Collapse in={openSettings}>
                                    {watchServiceType ===
                                        MB_SERVICE_CLASSIFICATION_SIMPLE && (
                                        <Grid container item spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <Controller
                                                    name="maxBookings"
                                                    control={control}
                                                    rules={{
                                                        validate: (value) => {
                                                            if (
                                                                value === "" ||
                                                                value == null
                                                            )
                                                                return true;
                                                            const num =
                                                                Number(value);
                                                            if (
                                                                !Number.isInteger(
                                                                    num
                                                                ) ||
                                                                num < 1
                                                            )
                                                                return "Must be a positive integer";
                                                            return true;
                                                        }
                                                    }}
                                                    render={({
                                                        field,
                                                        fieldState
                                                    }) => (
                                                        <TextField
                                                            {...field}
                                                            label="Max bookings"
                                                            size="small"
                                                            error={
                                                                !!fieldState.error
                                                            }
                                                            helperText={
                                                                fieldState.error
                                                                    ?.message
                                                            }
                                                            InputLabelProps={{
                                                                shrink: true
                                                            }}
                                                            {...numericInputProps}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Box
                                                sx={{
                                                    paddingTop: "15px",
                                                    paddingLeft: "10px",
                                                    display: "flex",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <InfoTooltip title="Set the maximum number of scheduled bookings allowed for this service. Once reached, this service will no longer be available for online booking. Leave empty for unlimited." />
                                            </Box>
                                            {isEditing &&
                                                watch("maxBookings") !== "" &&
                                                watch("maxBookings") !=
                                                    null && (
                                                    <Box
                                                        sx={{
                                                            paddingTop: "15px",
                                                            paddingLeft: "16px",
                                                            display: "flex",
                                                            alignItems: "center"
                                                        }}
                                                    >
                                                        <Typography variant="body2">
                                                            Bookings remaining:{" "}
                                                            <strong>
                                                                {Number(
                                                                    watch(
                                                                        "maxBookings"
                                                                    )
                                                                ) -
                                                                    (watch(
                                                                        "scheduledBookings"
                                                                    ) || 0)}
                                                            </strong>
                                                        </Typography>
                                                    </Box>
                                                )}
                                        </Grid>
                                    )}
                                    <Grid container item spacing={2}>
                                        <Grid item xs={12} sm={8}>
                                            <Controller
                                                name="exemptServiceFees"
                                                control={control}
                                                render={({
                                                    field: { onChange, value }
                                                }) => (
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={value}
                                                                value={value}
                                                                onChange={
                                                                    onChange
                                                                }
                                                            />
                                                        }
                                                        label="Exempt service fee"
                                                    />
                                                )}
                                            />

                                            <InfoTooltip title="Overrides any company level service fee" />
                                        </Grid>
                                    </Grid>
                                </Collapse>
                                {allCoursesCompleted() && (
                                    <Alert
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "6px 16px",
                                            marginTop: "16px",
                                            marginBottom: "16px",
                                            border: `1px solid ${theme.palette.info.main}`,
                                            backgroundColor:
                                                theme.palette.info.lightest,
                                            textAlign: "center"
                                        }}
                                        severity="info"
                                        icon={<Info fontSize="inherit" />}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight={500}
                                            display={"inline"}
                                            color={theme.palette.info.main}
                                        >
                                            All start dates for this course have
                                            been completed
                                        </Typography>
                                    </Alert>
                                )}
                                {watchServiceType ===
                                    MB_SERVICE_CLASSIFICATION_GROUP && (
                                    <>
                                        {scheduleFields.map(
                                            (field, sectionIdx) =>
                                                (!field.endDateHasPassed ||
                                                    (field.endDateHasPassed &&
                                                        showCompletedDates)) && (
                                                    <Grid
                                                        container
                                                        item
                                                        spacing={2}
                                                        key={field.scheduleId}
                                                    >
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            sm={12}
                                                        >
                                                            <CoursesrRuleSection
                                                                control={
                                                                    control
                                                                }
                                                                setValue={
                                                                    setValue
                                                                }
                                                                getValues={
                                                                    getValues
                                                                }
                                                                sectionIdx={
                                                                    sectionIdx
                                                                }
                                                                scheduleId={
                                                                    field.scheduleId
                                                                }
                                                                formState={{
                                                                    errors
                                                                }}
                                                                companyId={
                                                                    mbxUser.companyId
                                                                }
                                                                endDateHasPassed={
                                                                    field.endDateHasPassed
                                                                }
                                                                handleDeleteDateRange={
                                                                    handleDeleteDateRange
                                                                }
                                                                setShowCourseDatesModal={
                                                                    setShowCourseDatesModal
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )
                                        )}
                                        <Grid container item spacing={2}>
                                            <Grid item xs={12}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "12px"
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        startIcon={<AddIcon />}
                                                        onClick={
                                                            handleAddRuleSection
                                                        }
                                                    >
                                                        Add start date
                                                    </Button>

                                                    <InfoTooltip
                                                        title={`Create different start dates for the same course with the same settings, e.g. "Summer session", "Fall session", etc; each start date can have its own provider assignment`}
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    sx={{
                                        marginTop:
                                            openTaxSettings ||
                                            watchServiceType ===
                                                MB_SERVICE_CLASSIFICATION_GROUP
                                                ? "18px"
                                                : "12px"
                                    }}
                                >
                                    <Button
                                        onClick={() => {
                                            setConfirmationFunction(() =>
                                                handleActivenessChangeOfService(
                                                    !activeValue
                                                )
                                            );
                                            setConfirmationDialogContent(
                                                activeValue ? (
                                                    <Typography>
                                                        Are you sure you wish to
                                                        deactivate this service?
                                                    </Typography>
                                                ) : (
                                                    <Typography>
                                                        Are you sure you wish to
                                                        activate this service?
                                                    </Typography>
                                                )
                                            );
                                            setConformationDialogTitle(
                                                activeValue
                                                    ? "Deactivate service"
                                                    : "Activate service"
                                            );
                                            setConformationDialogSubmitButton(
                                                activeValue
                                                    ? "Deactivate"
                                                    : "Activate"
                                            );
                                            setConfirmationDialogOpen(true);
                                        }}
                                        variant="text"
                                        disabled={!selectedService}
                                        color="primary"
                                        sx={{
                                            "&:hover": {
                                                backgroundColor:
                                                    "transparent !important"
                                            }
                                        }}
                                        disableRipple
                                    >
                                        <Typography
                                            variant="button"
                                            sx={{
                                                textTransform: "none",
                                                lineHeight: "14px"
                                            }}
                                        >
                                            {activeValue
                                                ? "Deactivate service"
                                                : "Activate service"}
                                        </Typography>
                                    </Button>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    sx={{
                                        marginTop:
                                            watchServiceType ===
                                            MB_SERVICE_CLASSIFICATION_GROUP
                                                ? "18px"
                                                : "20px"
                                    }}
                                >
                                    <Button
                                        onClick={() => {
                                            setConfirmationFunction(() =>
                                                handleDeleteService()
                                            );
                                            setConfirmationDialogContent(
                                                <Typography>
                                                    Are you sure you want to
                                                    delete this service?
                                                </Typography>
                                            );
                                            setConformationDialogTitle(
                                                "Delete service"
                                            );
                                            setConformationDialogSubmitButton(
                                                "Delete"
                                            );
                                            setConfirmationDialogOpen(true);
                                        }}
                                        variant="text"
                                        disabled={!selectedService}
                                        color="error"
                                        sx={{
                                            "&:hover": {
                                                backgroundColor:
                                                    "transparent !important"
                                            }
                                        }}
                                        disableRipple
                                    >
                                        <Typography
                                            variant="button"
                                            sx={{
                                                textTransform: "none",
                                                lineHeight: "14px"
                                            }}
                                        >
                                            {`Delete service`}
                                        </Typography>
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {watchServiceType ===
                            MB_SERVICE_CLASSIFICATION_GROUP && (
                            <Grid
                                container
                                item
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start"
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showCompletedDates}
                                            onChange={(event) =>
                                                setShowCompletedDates(
                                                    event.target.checked
                                                )
                                            }
                                            value={showCompletedDates}
                                        />
                                    }
                                    label="Show completed dates"
                                />
                            </Grid>
                        )}
                        <Grid
                            container
                            item
                            gap="18px"
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end"
                            }}
                        >
                            <Button
                                onClick={handleClose}
                                variant="cancelButton"
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                color="primary"
                                variant="contained"
                                type="submit"
                                loading={isSaving}
                                disabled={
                                    !isValid ||
                                    Object.keys(dirtyFields).length === 0 ||
                                    !requiredFieldsValid()
                                }
                            >
                                Save
                            </LoadingButton>
                        </Grid>
                    </DialogActions>
                </form>
            </Dialog>
        </ThemeProvider>
    );
};
